/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * @typedef {import('swiper').Swiper} Swiper
 *
 * Banner autoplay helper.
 *
 * Swiper tracks `running` and `paused` independently. `stop()` clears timers but leaves
 * `paused` set, so a later `resume()` (from transitionend) can no-op and strand autoplay.
 * Always go through this helper so `paused` is normalised alongside `running`.
 *
 * Toggle uses `pause`/`resume` (not `stop`/`start`) so the thumb progress overlay keeps the
 * banked remainder across a user pause.
 */
export const bannerAutoplayHelper = {
  /**
   * @param {Swiper} swiper
   *
   * @returns {boolean}
   */
  isUserPaused(swiper) {
    return !!swiper.banner?.userPaused;
  },

  /**
   * Stop autoplay and clear the paused flag Swiper leaves behind.
   *
   * @param {Swiper} swiper
   */
  stop(swiper) {
    if (!swiper.autoplay) {
      return;
    }

    swiper.autoplay.stop();
    swiper.autoplay.paused = false;
  },

  /**
   * Restart autoplay from a clean state, honouring the corner toggle.
   *
   * @param {Swiper} swiper
   */
  start(swiper) {
    if (!swiper.autoplay || this.isUserPaused(swiper)) {
      return;
    }

    swiper.autoplay.paused = false;
    swiper.autoplay.stop();
    swiper.autoplay.start();
  },

  /**
   * Hold the current autoplay window instead of discarding it.
   *
   * Called with no arguments on purpose: that flags the pause as interaction-driven, which is
   * what makes `resume()` run the banked remainder rather than a fresh delay.
   *
   * @param {Swiper} swiper
   */
  pause(swiper) {
    const autoplay = swiper.autoplay;

    if (!autoplay?.running) {
      return;
    }

    // Already holding a remainder (e.g. re-assert after Swiper auto-resumed) — keep it.
    if (autoplay.paused && autoplay.timeLeft > 0) {
      return;
    }

    // Nothing worth banking — fall back to stop:
    //  - mid-transition (remainder clamps to 0 → instant advance on resume)
    //  - last-slide isEnd clamp that leaves pause armed with no resume path
    if (autoplay.timeLeft <= 0 || swiper.animating) {
      this.stop(swiper);

      return;
    }

    autoplay.pause();
  },

  /**
   * Run out the window banked by `pause()`, or start a fresh one when there is none.
   *
   * @param {Swiper} swiper
   */
  resume(swiper) {
    if (!swiper.autoplay || this.isUserPaused(swiper)) {
      return;
    }

    if (!swiper.autoplay.running) {
      this.start(swiper);

      return;
    }

    swiper.autoplay.resume();
  }
};
